import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import VaultEmptyState from '../VaultCollection/VaultEmptyState'
import PresentationTable from '../../PresentationTable'
import { useAppContext } from '../../../../redux/slices/appContext'
import { ICON_NAMES } from '../../../../constants'
import { useListOrionClientDocuments } from './hooks'
import { useStyles } from './styles'
import { getColumnConfig } from './columnConfig'

export const OrionDocumentList = ({
  fileType,
  excludedLocation,
  category,
  folder,
  groupBy,
  defaultExpanded = true,
  columnConfig,
  defaultSortBy = [{ id: 'createdDate', desc: true }],
  textSearch
}) => {
  const { clientId } = useAppContext()

  const categories = useMemo(() => Array.isArray(category) ? category : (category ? [category] : []), [category])

  const classes = useStyles()
  const columns = getColumnConfig(columnConfig)

  const { data, isLoading } = useListOrionClientDocuments({
    clientId,
    fileType,
    excludedLocation
  }, { enabled: !!clientId })

  const displayData = useMemo(() => {
    if (!data?.length) return []

    const filteredData = data.filter(item => {
      if (categories?.length && !categories.includes(item.category)) {
        return false
      }
      if (folder?.length && item.folder !== folder) {
        return false
      }
      if (textSearch?.length) {
        const search = textSearch.toLowerCase()
        const searchable = [item.blobText, item.blobDesc, item.category]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!searchable.includes(search)) {
          return false
        }
      }
      return true
    })

    if (groupBy?.length && !textSearch?.length) {
      const groupedData = filteredData.reduce((acc, item) => {
        const key = item[groupBy] ?? 'Ungrouped'
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(item)
        return acc
      }, {})

      return Object.entries(groupedData).map(([key, items]) => ({
        blobText: key,
        subRows: items.map((item, index) => ({
          ...item,
          _next: null
        }))
      }))
    }
    return filteredData
  }, [data, groupBy, categories, folder, textSearch])

  return (
    <>
      <div className={classes.tableContainer}>
        {!isLoading && !data?.length ? (
          <VaultEmptyState
            icon={ICON_NAMES.paperMilestones}
            title='No orion documents found for this client'
            subtitle='There are no documents to display'
          />
        ) : !isLoading && textSearch?.length && !displayData?.length ? (
          <VaultEmptyState
            icon={ICON_NAMES.paperMilestones}
            title='No documents match your search'
            subtitle='Try adjusting your search terms'
          />
        ) : (
          <PresentationTable.Wrapper>
            <PresentationTable
              className={classes.vaultListTable}
              data={displayData ?? []}
              sortable
              expandable={!!groupBy?.length && !textSearch?.length}
              defaultExpanded={defaultExpanded}
              loading={isLoading}
              columns={columns}
              defaultSort={defaultSortBy}
            />
          </PresentationTable.Wrapper>
        )}
      </div>
    </>
  )
}

OrionDocumentList.propTypes = {
  fileType: PropTypes.string,
  excludedLocation: PropTypes.string,
  category: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  folder: PropTypes.string,
  groupBy: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  columnConfig: PropTypes.arrayOf(PropTypes.object),
  defaultSortBy: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool
  })),
  textSearch: PropTypes.string
}
